import React from 'react';
import { HuchaPosventaValues, HuchaVentaValues } from '../models/hucha-values';
import { PortalContext } from '../../../../../context/portal-context';
import { FocusLayerSizeV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import { useBonoPosventaTracker } from '../../bono-posventa/hooks/use-bono-posventa-tracker';
import { useHuchaTracker } from '../../hucha-venta/hooks/use-hucha-tracker';
import { PopupHuchaContent } from '../components/popup-hucha-content';

// hook para manejar el popup/layer de la hucha
export const useHuchaPopup = ({
	huchaValues,
	layerContent,
}: {
	huchaValues: HuchaVentaValues | HuchaPosventaValues | null;
	layerContent: () => JSX.Element | null;
}) => {
	const portalContext = React.useContext(PortalContext);
	const { portalService, layerManager } = portalContext;

	const [isPopupVisible, setIsPopupVisible] = React.useState(false);
	const layerRef = React.useRef<any>(null);
	const hasTrackedLayerLoadRef = React.useRef(false);
	const userVehicles = portalService.getPersonVehicles().getValue();

	const isPosventa = huchaValues?.huchaType === 'posventa';
	const huchaTracker = useHuchaTracker((huchaValues as HuchaVentaValues | null)?.huchaTier ?? 'standard');
	const posventaTracker = useBonoPosventaTracker();
	const { trackOpenLayerClick, trackLayerLoad, trackLayerCloseClick } = isPosventa ? posventaTracker : huchaTracker;

	const handlePopupOpen = () => {
		hasTrackedLayerLoadRef.current = false;
		trackOpenLayerClick();
		setIsPopupVisible(true);
	};

	React.useEffect(() => {
		if (userVehicles && isPopupVisible && !hasTrackedLayerLoadRef.current) {
			hasTrackedLayerLoadRef.current = true;
			trackLayerLoad();
		}
	}, [userVehicles, isPopupVisible, trackLayerLoad]);

	const handlePopupClose = () => {
		hasTrackedLayerLoadRef.current = false;
		setIsPopupVisible(false);
		trackLayerCloseClick();
	};

	React.useEffect(() => {
		if (!isPopupVisible) return;

		const layer = layerManager.openFocusLayer(
			() => (
				<PopupHuchaContent
					portalContext={portalContext}
					handlePopupClose={handlePopupClose}
					layerContent={layerContent}
				/>
			),
			{},
			{
				size: FocusLayerSizeV2.A,
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
			},
		);

		layerRef.current = layer;

		return () => {
			hasTrackedLayerLoadRef.current = false;
			layer.close();
			layerRef.current = null;
		};
	}, [isPopupVisible, huchaValues, portalContext, layerManager, layerContent]);

	return {
		isPopupVisible,
		handlePopupOpen,
		handlePopupClose,
	};
};
